import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

const STORAGE_KEY = 'rager-chat:layout'

interface StoredLayout {
  leftCollapsed?: boolean
  rightCollapsed?: boolean
}

function readStoredLayout(): StoredLayout {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredLayout
    return {
      leftCollapsed: typeof parsed.leftCollapsed === 'boolean' ? parsed.leftCollapsed : undefined,
      rightCollapsed: typeof parsed.rightCollapsed === 'boolean' ? parsed.rightCollapsed : undefined,
    }
  } catch {
    return {}
  }
}

export type WorkspaceView = 'chat' | 'documents' | 'reference-library'

const WORKSPACE_VIEWS: readonly WorkspaceView[] = [
  'chat',
  'documents',
  'reference-library',
]

/**
 * Phase 3B — the active workspace section is mirrored in the URL hash
 * (``#documents`` / ``#reference-library`` / ``#chat``) so a browser refresh
 * (F5) keeps the user on the same screen instead of bouncing back to chat.
 * The hash holds ONLY the non-sensitive view name — never tokens or data
 * (auth stays in HttpOnly cookies). The app has no router, so a hash is the
 * smallest, lowest-risk persistence mechanism. An unknown/empty hash falls
 * back to the default ``chat`` view.
 */
function readWorkspaceFromHash(): WorkspaceView | null {
  if (typeof window === 'undefined') return null
  const raw = window.location.hash.replace(/^#\/?/, '').trim()
  return (WORKSPACE_VIEWS as readonly string[]).includes(raw)
    ? (raw as WorkspaceView)
    : null
}

interface AppState {
  // layout
  leftCollapsed: boolean
  rightCollapsed: boolean
  toggleLeft: () => void
  toggleRight: () => void

  // workspace routing — mirrored to the URL hash so F5 keeps the section
  // (Phase 3B). Never written to localStorage; the hash holds only the view name.
  activeWorkspace: WorkspaceView
  setWorkspace: (next: WorkspaceView) => void

  // settings placeholder modal
  settingsOpen: boolean
  openSettings: () => void
  closeSettings: () => void

  // Step 13.7-FIX — composer overlays (case Scope Selector / Reference Picker).
  // Their open-state is lifted here (was local to Composer) so recovery
  // actions inside a no_context assistant message can open them. The overlays
  // themselves stay rendered/anchored inside the Composer.
  scopeSelectorOpen: boolean
  openScopeSelector: () => void
  closeScopeSelector: () => void
  toggleScopeSelector: () => void
  referencePickerOpen: boolean
  openReferencePicker: () => void
  closeReferencePicker: () => void
  toggleReferencePicker: () => void
}

const AppStateContext = createContext<AppState | null>(null)

interface ProviderProps {
  children: ReactNode
}

export function AppStateProvider({ children }: ProviderProps) {
  const stored = readStoredLayout()
  const [leftCollapsed, setLeftCollapsed] = useState<boolean>(stored.leftCollapsed ?? false)
  const [rightCollapsed, setRightCollapsed] = useState<boolean>(stored.rightCollapsed ?? false)
  // Initialize from the URL hash so a refresh restores the current section.
  const [activeWorkspace, setWorkspace] = useState<WorkspaceView>(
    () => readWorkspaceFromHash() ?? 'chat',
  )
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [scopeSelectorOpen, setScopeSelectorOpen] = useState(false)
  const [referencePickerOpen, setReferencePickerOpen] = useState(false)

  // Sidebar collapse state is the only piece persisted to localStorage,
  // intentionally — refreshes shouldn't undo the user's layout choice.
  // Auth tokens are NEVER stored here; cookies are the only auth surface.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ leftCollapsed, rightCollapsed } satisfies StoredLayout),
      )
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [leftCollapsed, rightCollapsed])

  // Mirror the active section into the URL hash so F5 restores it. Uses
  // replaceState (no hashchange event, no new history entry) so it never loops
  // with the listener below and never pollutes the back stack. Path + query
  // (e.g. the invite ``?token=…``) are preserved — only the fragment changes.
  useEffect(() => {
    if (typeof window === 'undefined') return
    // Treat an absent/unknown hash as the default 'chat' so the initial render
    // doesn't write a redundant #chat.
    const current = readWorkspaceFromHash() ?? 'chat'
    if (current === activeWorkspace) return
    try {
      const url = new URL(window.location.href)
      // Keep the URL clean on the default view; only sub-sections carry a hash.
      url.hash = activeWorkspace === 'chat' ? '' : activeWorkspace
      window.history.replaceState(window.history.state, '', url)
    } catch {
      // ignore — hash sync is best-effort, navigation still works in-memory
    }
  }, [activeWorkspace])

  // Keep state in sync when the hash changes outside React (browser
  // back/forward, a hash link, or a manual edit). Guarded so it only updates
  // on a real difference.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onHashChange = () => {
      const fromHash = readWorkspaceFromHash() ?? 'chat'
      setWorkspace((prev) => (prev === fromHash ? prev : fromHash))
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const toggleLeft = useCallback(() => setLeftCollapsed((v) => !v), [])
  const toggleRight = useCallback(() => setRightCollapsed((v) => !v), [])

  const openSettings = useCallback(() => setSettingsOpen(true), [])
  const closeSettings = useCallback(() => setSettingsOpen(false), [])

  const openScopeSelector = useCallback(() => setScopeSelectorOpen(true), [])
  const closeScopeSelector = useCallback(() => setScopeSelectorOpen(false), [])
  const toggleScopeSelector = useCallback(
    () => setScopeSelectorOpen((v) => !v),
    [],
  )
  const openReferencePicker = useCallback(
    () => setReferencePickerOpen(true),
    [],
  )
  const closeReferencePicker = useCallback(
    () => setReferencePickerOpen(false),
    [],
  )
  const toggleReferencePicker = useCallback(
    () => setReferencePickerOpen((v) => !v),
    [],
  )

  const value = useMemo<AppState>(
    () => ({
      leftCollapsed,
      rightCollapsed,
      toggleLeft,
      toggleRight,
      activeWorkspace,
      setWorkspace,
      settingsOpen,
      openSettings,
      closeSettings,
      scopeSelectorOpen,
      openScopeSelector,
      closeScopeSelector,
      toggleScopeSelector,
      referencePickerOpen,
      openReferencePicker,
      closeReferencePicker,
      toggleReferencePicker,
    }),
    [
      leftCollapsed,
      rightCollapsed,
      toggleLeft,
      toggleRight,
      activeWorkspace,
      settingsOpen,
      openSettings,
      closeSettings,
      scopeSelectorOpen,
      openScopeSelector,
      closeScopeSelector,
      toggleScopeSelector,
      referencePickerOpen,
      openReferencePicker,
      closeReferencePicker,
      toggleReferencePicker,
    ],
  )

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useAppState(): AppState {
  const ctx = useContext(AppStateContext)
  if (!ctx) {
    throw new Error('useAppState must be used inside <AppStateProvider>')
  }
  return ctx
}
